import type { TossPaymentsSDK } from '@tosspayments/tosspayments-sdk';

/**
 * Toss 결제창 호출 규약. web(스토어·마이페이지)과 console-web(좌석·정산)이
 * **같은 SDK 인자**를 써야 한다 — `card` 옵션 하나만 갈라져도 한쪽에서만
 * 앱카드 전용 창이 뜨는 식으로 조용히 어긋난다.
 *
 * SDK 로더는 앱이 주입한다. 클라이언트 키가 앱별 env 라 패키지가 들고 있을 수 없다.
 * 단건과 자동결제(빌링)는 Toss 상점(MID)·클라이언트 키가 달라 팩토리도 둘이다 —
 * 단건 키로 카드 등록을 하면 발급된 빌링키를 서버가 자동결제 키로 쓰지 못한다.
 */

/**
 * Toss Payments orderId 최대 길이(6~64자).
 *
 * 서버(`billing-service` TOSS_ORDER_ID_MAX_LENGTH)와 **같은 값**이어야 한다.
 * 프론트가 만든 orderId 로 서버 결제레코드를 생성하므로, 여기서 자르는 길이가
 * 달라지면 confirm 단계에서 orderId 불일치로 조회가 실패한다.
 */
export const TOSS_ORDER_ID_MAX_LENGTH = 64;

/** 주문 ID 생성. 서버에 만든 결제레코드의 orderId 와 **같은 값**이어야 한다. */
export function generateOrderId(prefix = 'ord'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : Math.random().toString(36).slice(2);
  return `${prefix}_${Date.now()}_${rand}`.slice(0, TOSS_ORDER_ID_MAX_LENGTH);
}

/**
 * 사용자가 결제창을 그냥 닫은 경우. Toss SDK 는 Error 가 아니라 `{ code, message }` 로
 * reject 하므로 일반 오류와 구분되지 않는다 — 취소를 오류로 안내하면 안 된다.
 */
const TOSS_CANCEL_CODES = new Set(['USER_CANCEL', 'PAY_PROCESS_CANCELED']);

export function isTossUserCancel(e: unknown): boolean {
  if (e instanceof Error) return false;
  if (typeof e !== 'object' || e === null || !('code' in e)) return false;
  const code = (e as { code?: unknown }).code;
  return typeof code === 'string' && TOSS_CANCEL_CODES.has(code);
}

/** 결제창 성공/실패 후 돌아올 절대 URL. */
export interface TossRedirectUrls {
  successUrl: string;
  failUrl: string;
}

export interface TossCardPaymentParams extends TossRedirectUrls {
  customerKey: string;
  amount: number;
  /** 서버에 사전 생성한 결제레코드의 orderId. Toss orderId 와 동일해야 한다. */
  orderId: string;
  orderName: string;
  customerEmail?: string;
  customerName?: string;
  customerMobilePhone?: string;
}

export interface TossBillingAuthParams extends TossRedirectUrls {
  customerKey: string;
  customerEmail?: string;
  customerName?: string;
}

/**
 * 단건 결제창.
 *
 * @param getSdk 앱의 **단건** SDK 싱글톤 로더(`getTossPayments`).
 *
 * @example
 * const toss = createTossCheckout(getTossPayments);
 * await toss.requestCardPayment({ ...common, successUrl, failUrl });
 */
export function createTossCheckout(getSdk: () => Promise<TossPaymentsSDK>) {
  return {
    /**
     * 일시결제 창 오픈(개인 구독·기업 좌석·정산금 공통).
     *
     * 호출 전 서버에 READY 결제 레코드가 있어야 한다 — 금액을 화면 값으로 보내면 변조된다.
     * ⚠️ successUrl 은 결제를 **시작한 origin** 이어야 한다. 다른 앱으로 보내면 그쪽이
     * 승인 쿼리를 받아 처리하게 되고, 원래 화면은 결과를 영영 못 받는다.
     */
    requestCardPayment: async (params: TossCardPaymentParams): Promise<void> => {
      const sdk = await getSdk();
      const payment = sdk.payment({ customerKey: params.customerKey });
      await payment.requestPayment({
        method: 'CARD',
        amount: { currency: 'KRW', value: params.amount },
        orderId: params.orderId,
        orderName: params.orderName,
        successUrl: params.successUrl,
        failUrl: params.failUrl,
        customerEmail: params.customerEmail,
        customerName: params.customerName,
        customerMobilePhone: params.customerMobilePhone,
        card: {
          useEscrow: false,
          flowMode: 'DEFAULT',
          useCardPoint: false,
          useAppCardOnly: false,
        },
      });
    },
  };
}

export type TossCheckout = ReturnType<typeof createTossCheckout>;

/**
 * 자동결제(빌링) 카드 등록창.
 *
 * @param getSdk 앱의 **자동결제** SDK 싱글톤 로더(`getTossBillingPayments`).
 *
 * @example
 * const billing = createTossBillingAuth(getTossBillingPayments);
 * await billing.requestBillingAuth({ customerKey, successUrl, failUrl });
 */
export function createTossBillingAuth(getSdk: () => Promise<TossPaymentsSDK>) {
  return {
    /**
     * 자동결제(빌링) 카드 등록.
     *
     * ⚠️ successUrl/failUrl 은 반드시 **호출한 앱의 origin**. billing intent 를
     * sessionStorage 로 넘기는데 sessionStorage 는 origin 별로 격리된다 —
     * 다른 origin 으로 돌아오면 intent 를 못 읽어 성공 화면이 분기하지 못한다.
     */
    requestBillingAuth: async (params: TossBillingAuthParams): Promise<void> => {
      const sdk = await getSdk();
      const payment = sdk.payment({ customerKey: params.customerKey });
      await payment.requestBillingAuth({
        method: 'CARD',
        successUrl: params.successUrl,
        failUrl: params.failUrl,
        customerEmail: params.customerEmail,
        customerName: params.customerName,
      });
    },
  };
}

export type TossBillingAuth = ReturnType<typeof createTossBillingAuth>;
